/**
 * ObjectDetailsGrid Component
 *
 * Displays object fields in a grid format with smart formatting
 * for dates, JSON objects, and other data types.
 */

import React from 'react';
import { IdLink } from './IdLink';
import { isIdField, looksLikeDbId, inferObjectTypeFromFieldName } from '../utils/idDetection';
import { encodeGlobalId, isGlobalId, decodeGlobalId } from '@206mp/shared/utils/globalObjectId';
import logger from '../utils/logger';

interface ObjectDetailsGridProps {
  object: Record<string, any>;
  /** Object type for handling self-referential foreign keys like parentId */
  objectType?: string;
}

/**
 * Render ID field as clickable link or plain value
 */
function RenderIdField({
  fieldName,
  value,
  contextObjectType
}: {
  fieldName: string;
  value: any;
  contextObjectType?: string;
}): React.ReactNode {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <span className="text-muted">—</span>;
  }

  // Check if value is already a global ID (from GraphQL responses)
  if (typeof value === 'string' && isGlobalId(value)) {
    const decoded = decodeGlobalId(value);
    if (!('error' in decoded)) {
      return <IdLink globalId={value} typeName={decoded.type} />;
    }
  }

  // Must be a number and look like a database ID (for REST responses)
  if (!looksLikeDbId(value)) {
    return <span className="font-mono text-muted">{String(value)}</span>;
  }

  // Try to infer object type from field name
  let objectType = inferObjectTypeFromFieldName(fieldName);

  // Special case: parentId refers to the same type as the current object
  if (fieldName.toLowerCase() === 'parentid' && contextObjectType) {
    objectType = contextObjectType as any;
  }

  if (!objectType) {
    // Cannot determine type - render as plain numeric value
    return <span className="font-mono text-muted">{value}</span>;
  }

  // Try to encode as global ID
  try {
    // Convert string to number if needed (API returns IDs as strings)
    const numericId = typeof value === 'string' ? parseInt(value, 10) : value;
    const globalId = encodeGlobalId(objectType, numericId);
    return <IdLink globalId={globalId} typeName={objectType} />;
  } catch (error) {
    // Encoding failed - render as plain numeric value
    logger.debug('ObjectDetailsGrid', `Failed to encode ${objectType} ID ${value}:`, error);
    return <span className="font-mono text-muted">{value}</span>;
  }
}

/**
 * Format a value for display based on its type
 */
function formatValue(key: string, value: any, contextObjectType?: string): React.ReactNode {
  // Handle ID fields first - render as clickable links
  if (isIdField(key)) {
    return <RenderIdField fieldName={key} value={value} contextObjectType={contextObjectType} />;
  }

  // Handle null/undefined
  if (value === null) return <span className="text-muted italic">null</span>;
  if (value === undefined) return <span className="text-muted italic">undefined</span>;

  // Handle dates (keys ending with _at or containing 'date')
  if ((key.endsWith('At') || key.includes('date')) && typeof value === 'string') {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return (
          <div>
            <div>{date.toLocaleString()}</div>
            <div className="text-sm text-muted">{value}</div>
          </div>
        );
      }
    } catch {
      // Not a valid date, fall through
    }
  }

  // Handle objects and arrays (pretty-print as JSON)
  if (typeof value === 'object') {
    return (
      <pre className="bg-surface-2 p-2 rounded text-sm overflow-x-auto max-h-96 overflow-y-auto">
        <code>{JSON.stringify(value, null, 2)}</code>
      </pre>
    );
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-success font-medium' : 'text-muted'}>
        {value ? 'true' : 'false'}
      </span>
    );
  }

  // Handle numbers
  if (typeof value === 'number') {
    return <span className="font-mono">{value.toLocaleString()}</span>;
  }

  // Handle strings (default)
  return <span>{String(value)}</span>;
}

export function ObjectDetailsGrid({ object, objectType }: ObjectDetailsGridProps) {
  const entries = Object.entries(object);

  if (entries.length === 0) {
    return (
      <div className="text-muted italic p-4">
        No fields to display
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-surface-2">
            <th className="text-left p-3 font-semibold text-foreground border-b border-border w-1/3">
              Field
            </th>
            <th className="text-left p-3 font-semibold text-foreground border-b border-border">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, value], index) => (
            <tr
              key={key}
              className={index % 2 === 0 ? 'bg-surface-1' : 'bg-background'}
            >
              <td className="p-3 font-mono text-sm text-foreground border-b border-border align-top">
                {key}
              </td>
              <td className="p-3 text-foreground border-b border-border break-words">
                {formatValue(key, value, objectType)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
